import { AddEditProps, ReturnedItem } from '../../models/table-models';
import { InventoryContentForTask, ItemState } from '../../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
    checkForItemsInInventory, returnItemToUser,
    useGetAllScraps,
    useGetAllShortOtherMaterialPassports,
    useGetReferences,
    useGetReferencesForDistributor, useGetReturnedItems,
    useGetUserInventory,
    useGetWarehouses,
    scrapItem,
} from '../../utils/warehouses-hooks';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import React, {useMemo, useState} from 'react';
import { TableRequestParams } from '../../utils/data-hooks';
import {useGetUsers} from "../../utils/users-hooks";

type AddEditInventoryProps = Omit<AddEditProps<ReturnedItem>, 'itemToEdit'> & {
    itemToEdit?: ReturnedItem | ReturnedItem[];
};

export const AddEditInventory = ({ itemToEdit, setShowModal, refetchEntitiesHook }: AddEditInventoryProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { handleSubmit, control, resetField } = useForm<InventoryContentForTask>({
        mode: 'all',
    });
    const itemsToEdit = useMemo(
        () => (Array.isArray(itemToEdit) ? itemToEdit : itemToEdit ? [itemToEdit] : []),
        [itemToEdit],
    );

    const [toWarehouse, setToWarehouse] = useState<boolean>(true);
    const [getAllUsers] = useAsyncResource(useGetUsers, {} as TableRequestParams);
    const [getAllWarehouses] = useAsyncResource(useGetWarehouses, {} as TableRequestParams);
    const useSubmitHandlerEdit = async (formValue: InventoryContentForTask) => {
        if (toWarehouse) {
            await sendToWarehouse(formValue);
        } else {
            await sendToDistributor(formValue);
        }
        refetchEntitiesHook();
        resourceCache(useGetUserInventory).clear();
        resourceCache(checkForItemsInInventory).clear();
        resourceCache(useGetAllShortOtherMaterialPassports).clear();
        resourceCache(useGetAllScraps).clear();
        resourceCache(useGetReferencesForDistributor).clear();
        resourceCache(useGetReferences).clear();
        resourceCache(useGetReturnedItems).clear();
        setShowModal(false);
    };

    const sendToWarehouse = async (formValue: InventoryContentForTask) => {
        await Promise.all(
            itemsToEdit.map((item) =>
                scrapItem({
                    ...formValue,
                    contentId: item.contentId ?? 0,
                    batchCode: item.batchCode ?? '',
                    cost: item.cost ?? 0,
                    count: item.count ?? 0,
                    passportId: item.passportId ?? 0,
                    id: item.id ?? 0,
                    state: item.state as ItemState,
                })
            )
        );
        toast.success(<CustomToastContent content={[t('itemReturned')]} whiteText />, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
        });
    }

    const sendToDistributor = async (formValue: InventoryContentForTask) => {
        const userId = formValue.userId;

        if (!userId) {

            toast.error(<CustomToastContent content={[t('addUser')]} whiteText />, {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
            });
            return;
        }
        if (itemsToEdit.length === 0 || itemsToEdit.some((item) => !item.id)) {

            toast.error(<CustomToastContent content={["Грешка не е намерен върнатия ресурс"]} whiteText />, {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
            });
            return;
        }
        await Promise.all(itemsToEdit.map((item) => returnItemToUser(item.id ?? 0, userId)));
        toast.success(<CustomToastContent content={[t('itemReturned')]} whiteText />, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
        });
    }

    return (
        <Form
            id='form'
            className='d-flex flex-column align-items-center p-4  w-100'
            noValidate
            onSubmit={handleSubmit(useSubmitHandlerEdit)}
        >
            <Form.Group className='d-flex mb-2 w-100 align-items-start'>
                <Form.Check
                    type='radio'
                    id='returnToWarehouse'
                    label={t('returnToWarehouse')}
                    name='returnOption'
                    checked={toWarehouse}
                    onChange={() => {
                        setToWarehouse(true);
                        resetField('userId');
                    }}
                />
                <Form.Check
                    type='radio'
                    id='returnToUser'
                    label={t('returnToUser')}
                    name='returnOption'
                    checked={!toWarehouse}
                    onChange={() => {
                        setToWarehouse(false);
                        resetField('warehouseId');
                    }}
                    className='ms-3'
                />
            </Form.Group>

            {toWarehouse ? (
                <Form.Group className=' d-flex flex-column mb-2 w-100 align-items-start ' controlId='warehouseId'>
                    <Form.Label>{t('forWarehouse')}</Form.Label>
                    <Controller
                        control={control}
                        name='warehouseId'
                        rules={{ required: toWarehouse }} // Make required only if returning to warehouse
                        render={({ field }) => (
                            <Select
                                className='w-100'
                                onChange={(newValue) => field.onChange(newValue?.id)}
                                options={getAllWarehouses().items}
                                getOptionLabel={({ warehouseName }) => warehouseName ?? ''}
                                getOptionValue={({ id }) => (id ? id.toString() : '')}
                            />
                        )}
                    />
                </Form.Group>
            ) : (
                <Form.Group className=' d-flex flex-column mb-2 w-100 align-items-start ' controlId='userId'>
                    <Form.Label>{t('forUser')}</Form.Label>
                    <Controller
                        control={control}
                        name='userId'
                        rules={{ required: !toWarehouse }} // Make required only if returning to user
                        render={({ field }) => (
                            <Select
                                className='w-100'
                                onChange={(newValue) => field.onChange(newValue?.id)}
                                options={getAllUsers().items}
                                getOptionLabel={({ name }) => name ?? ''}
                                getOptionValue={({ id }) => (id ? id.toString() : '')}
                            />
                        )}
                    />
                </Form.Group>
            )}
        </Form>
    );
};
